import {
  Ref,
  ref,
  shallowRef,
  computed,
  watch,
  onBeforeUnmount,
} from '@vue/composition-api';
import debounce from 'lodash.debounce';
import axios, { CancelTokenSource } from 'axios';

import type { SObject } from '@/types';
import type {
  RecordId,
  CompareOverviewRecord,
} from '@/services/SalesforceService';
import { FilterFacade } from '@/modules/filter/core/FilterFacade';
import useDI from '../useDI';

import CompareOverviewReader from './CompareOverviewReaderImpl';
import CachedCompareOverviewReader from './CachedCompareOverviewReader';
import FilterCompareOverviewReader from './FilterCompareOverviewReader';
import PageSeparator from './PageSeparator';
import CancelToken from './CancelToken';

let active: FilterCompareOverviewReader;
const map: Map<SObject, CachedCompareOverviewReader> = new Map();

export default function useRecords(
  recordSchema: Ref<{ sobject: SObject; customFields: string[] }>,
  filterFacade: Ref<FilterFacade<CompareOverviewRecord>>
) {
  const sobject = computed(() => recordSchema.value.sobject);

  const { container, Tokens } = useDI();
  const salesforceService = container.get(Tokens.SalesforceService);

  const records = shallowRef<CompareOverviewRecord[]>([]);
  const indexed = computed(() => {
    const res: Map<RecordId, CompareOverviewRecord> = new Map();
    for (const record of records.value) {
      res.set(record.Id, record);
    }
    return res;
  });
  const loading = ref(false);
  const done: Ref<Record<string, boolean>> = shallowRef({});
  const count = computed(() => records.value.length);
  const hasNextPage = computed(() => done.value[sobject.value] !== true);

  const searchTerm = ref('');
  let searchCancelTokenSource: null | CancelTokenSource = null;

  function cancel() {
    if (active !== undefined) {
      active.cancel();
    }

    if (searchCancelTokenSource !== null) {
      searchCancelTokenSource.cancel();
    }
  }

  async function search(value: string) {
    loading.value = true;
    try {
      const { cancelTokenSource, promise } = salesforceService.search(
        sobject.value,
        value,
        {
          customValueFields: recordSchema.value.customFields,
        }
      );
      searchCancelTokenSource = cancelTokenSource;
      const { records: compareOverviewRecords } = await promise;
      records.value = compareOverviewRecords;
      loading.value = false;
    } catch (e: unknown) {
      if (e instanceof axios.Cancel) {
        return;
      }
      loading.value = false;
      throw e;
    }
  }

  async function fetch() {
    let counter = 0;
    let acc: CompareOverviewRecord[] = [];
    do {
      const item = await active.getIterator().next();
      if (item.done === true) {
        done.value = { ...done.value, [sobject.value]: true };
        break;
      }
      if (item.value instanceof PageSeparator) {
        records.value = [...records.value, ...acc];
        acc = [];
        continue;
      }
      if (item.value instanceof CancelToken) {
        throw item.value;
      }
      acc.push(item.value);
      counter += 1;
    } while (counter < 50);

    records.value = [...records.value, ...acc];
  }

  async function load() {
    loading.value = true;
    try {
      await fetch();
      loading.value = false;
    } catch (e: unknown) {
      if (e instanceof CancelToken) {
        return;
      }
      loading.value = false;
      throw e;
    }
  }

  function onSObjectChanged() {
    cancel();

    done.value = { ...done.value, [sobject.value]: false };
    records.value = [];

    const reader = map.get(sobject.value);
    if (reader !== undefined) {
      active = new FilterCompareOverviewReader(reader, filterFacade.value);
    } else {
      const compareOverviewReader = new CompareOverviewReader(
        salesforceService,
        sobject.value,
        {
          customValueFields: recordSchema.value.customFields,
        }
      );
      const cachedCompareOverviewReader = new CachedCompareOverviewReader(
        compareOverviewReader
      );
      active = new FilterCompareOverviewReader(
        cachedCompareOverviewReader,
        filterFacade.value
      );
      map.set(sobject.value, cachedCompareOverviewReader);
    }

    load();
  }

  function refresh() {
    if (searchTerm.value !== '') {
      records.value = [];
      search(searchTerm.value);
      return;
    }
    map.delete(sobject.value);
    onSObjectChanged();
  }

  const debouncedOnSObjectChanged = debounce(onSObjectChanged, 100);

  watch([sobject, filterFacade], debouncedOnSObjectChanged);

  watch(recordSchema, (oldSchema, newSchema) => {
    const customFieldsChanged = oldSchema.sobject === newSchema.sobject;
    if (customFieldsChanged) {
      refresh();
    }
  });

  function searchWatcher(value: string) {
    cancel();
    records.value = [];

    if (value !== '') {
      search(value);
      return;
    }

    onSObjectChanged();
  }

  const debouncedSearchWatcher = debounce(searchWatcher, 400);

  watch(searchTerm, debouncedSearchWatcher);

  onSObjectChanged();

  onBeforeUnmount(cancel);

  return {
    records,
    indexed,
    count,
    loading,
    hasNextPage,
    searchTerm,
    load,
    refresh,
  };
}
